/**
 * Picklist Grouping Utility
 *
 * Organizes garments by SKU + Size for efficient picking.
 * Redos are grouped with same-SKU items.
 *
 * A garment that drew on the floor owner's backup stock carries `pickSku`, the
 * backup's code. The picker takes THAT shirt, so groups key on
 * `pickSku ?? sku` and a backup pile gets its own row.
 *
 * Single flat list - picker grabs everything at once, sorts by PT badge visually after labeling.
 * Pretreat info is on each label's QR code.
 *
 * ORDERING AUTHORITY (BLU-502): `groupPicklist` is the SOLE ordering path.
 * Groups are sorted with `buildPicklistComparator(config)` from
 * `@threadheads/shared`, and label print order is DERIVED from this output
 * (`getLabelsInPrintOrder` flat-maps `garmentIds`) — never re-sorted — so the
 * on-screen sheet, the A4 print, and the thermal label stack cannot diverge.
 *
 * Example: 5 × XL Black (1 redo) - picker grabs all 5 at once
 */

import {
  buildPicklistComparator,
  getSectionKey,
  getSectionLabel,
  DEFAULT_PICKLIST_SORT,
  type PicklistSortConfig,
} from '@threadheads/shared';
import { ORDER_CHANGE_BACKFILL_REASON } from '@/features/production-issues/types';

/**
 * Minimum interface for groupable garments
 */
export interface GroupableGarment {
  id: string;
  /** The ORDER's SKU. */
  sku: string;
  /**
   * The backup SKU the picker takes instead, when the unit drew on the floor
   * owner's backup stock under a different code. Absent otherwise.
   */
  pickSku?: string | null;
  /** The shirt taken carries a client's neck label (its code is reserved for that client). */
  clientLabelled?: boolean;
  /** That client's name, when known. */
  clientName?: string | null;
  /**
   * Original Shopify SKU (preserves `2`-prefix on dual SKUs + `CUST-` wrapper
   * on custom SKUs that are stripped by normalization in the `sku` field).
   * Required for BLU-435 DUAL/CUSTOM chip detection on PicklistRow.
   */
  shopifySku?: string | null;
  productName: string;
  size: string | null;
  color: string | null;
  isRedo?: boolean;
  redoReason?: string | null;
  /** From Shopify product Metafield - not derived from color */
  requiresPretreat?: boolean;
  orderNumber?: string;
  binLabel?: string | null;
  /**
   * Shopify product_type - from skus table. When the caller sorts on the
   * productType key it MUST project the alias-resolved DISPLAY value here
   * (the value PicklistRow renders), not the raw Shopify type, so two raw
   * types aliased to one display name form a contiguous run.
   */
  shopifyProductType?: string | null;
  /** Fulfillment type: 'POD' or 'P&P' */
  fulfillmentType?: string;
  /** Print technology for POD garments: 'DTG' | 'DTF'. Splits the POD block
   *  into POD·DTG / POD·DTF picking sections. Ignored for PAP. */
  printTechnology?: string | null;
  /** True when auto-added from resolved inventory error */
  isPriorityInjection?: boolean;
  /**
   * Catalogue photo of what the picker physically fetches: the blank for a
   * POD unit, the finished good for a PAP unit. Resolved server side against
   * the unit's OWN org. Null or absent means show nothing. A null member is
   * ignored when its group picks a photo, so it cannot veto a sibling's: any
   * "this unit must show nothing" rule has to be applied server side.
   */
  pickImageUrl?: string | null;
}

/**
 * A grouped row in the picklist showing aggregated SKU/Size data
 */
export interface PicklistGroup {
  /** Unique key for React rendering */
  groupKey: string;
  /**
   * SKU code the picker takes: the backup's when the group drew on one, else
   * the order's (normalized — `2`-prefix and `CUST-` wrapper stripped).
   */
  sku: string;
  /** The ORDER's SKU (first member). Differs from `sku` when the picker takes a backup. */
  unitSku: string;
  /** Every member's shirt carries a client's neck label (badge on the row). */
  clientLabelled: boolean;
  /** The client's name when the group is labelled and every member agrees on it, else null (generic badge). */
  clientName: string | null;
  /**
   * Original Shopify SKU from the first garment in the group. Used by
   * PicklistRow to detect CUSTOM (CUST-*) and standard-DUAL (2[A-Z]*) for
   * the BLU-435 chip cluster. Null when not plumbed by caller.
   */
  shopifySku: string | null;
  /** Product name for display */
  productName: string;
  /** Size (empty string if not applicable) */
  size: string;
  /**
   * Color. Empty string only when the group genuinely mixes colours or no
   * member has one — a group of ['', 'Black'] reports 'Black' (the first
   * NON-empty member colour), so it sorts/displays with its real colour
   * instead of landing in the empty-last tail.
   */
  color: string;
  /** Total garments in this group (including redos and order changes) */
  quantity: number;
  /**
   * How many items in this group are GENUINE redos (isRedo with any reason
   * other than the order-change backfill reason, including a null reason).
   * Order-change replacement units are counted in `orderChangeCount` instead.
   */
  redoCount: number;
  /**
   * How many items in this group are order-change replacement units
   * (isRedo AND redoReason === ORDER_CHANGE_BACKFILL_REASON). Disjoint from
   * `redoCount`; both are included in `quantity`.
   */
  orderChangeCount: number;
  /** Whether items require pretreat (from Shopify Metafield) - informational only */
  requiresPretreat: boolean;
  /** Genuine redo reasons present in this group (for display); never the order-change reason */
  redoReasons: string[];
  /** All garment IDs in this group (for label printing) */
  garmentIds: string[];
  /** Order numbers containing this SKU */
  orderNumbers: string[];
  /** Bin label if assigned */
  binLabel?: string | null;
  /** Shopify product_type (e.g., "T-Shirt", "Hoodie") - for POD items */
  shopifyProductType: string | null;
  /** Fulfillment type: 'POD' or 'P&P' */
  fulfillmentType: string;
  /** Print technology ('DTG' | 'DTF') for POD groups; 'DTG' fallback. PAP
   *  groups always report 'DTG' (inert — PAP is never sectioned by tech). */
  printTechnology: string;
  /** How many items in this group are priority injections */
  priorityInjectionCount: number;
  /**
   * The single distinct non-null photo among the members, or null. Zero
   * distinct photos, or more than one, is null: the group key has no org
   * component, so at a shared 3PL location owner and client units with the
   * same SKU string can share a group while their catalogues carry different
   * photos, and picking one of two disagreeing photos is worse than none.
   */
  pickImageUrl: string | null;
  /**
   * BOUNDARY key of the config's FIRST sort key for this group — the
   * band-header transition basis when `config.bandByFirstKey` is on, on the
   * SAME normalized equivalence basis the comparator sorts on (case-folded
   * colour, alias-mapped size in normalized/custom modes). Bands must key on
   * the COMPOSITE (fulfillmentType, sectionKey) pair. Display uses
   * `sectionLabel`.
   */
  sectionKey: string;
  /** RAW first-key value for the band-header label (never for boundaries). */
  sectionLabel: string;
}

/**
 * Flat grouped picklist - single list, no sections
 */
export interface GroupedPicklist {
  /** Batch ID */
  batchId: string;
  /** Total items in the batch */
  totalItems: number;
  /** Flat list of groups in configured picklist order */
  groups: PicklistGroup[];
  /** Summary counts */
  summary: {
    totalGroups: number;
    /** Genuine redos only (sum of group redoCount) */
    totalRedoCount: number;
    /** Order-change replacement units (sum of group orderChangeCount) */
    totalOrderChangeCount: number;
  };
}

/**
 * What a picklist row names the item as. The ONE definition, shared by both
 * row renderers (PicklistRow, and the read-only row in BatchDetailScreen) for
 * the visible text and the pick photo's subject alike.
 *
 *   P&P  the product name: the finished good IS what gets picked.
 *   POD  the garment category (alias-resolved Shopify product type), never the
 *        finished product's name, which would mislabel the blank the picker
 *        is holding. Falls back to the product name only when no product type
 *        is on file.
 */
export function picklistDisplayName(
  group: { productName: string; fulfillmentType: string; shopifyProductType: string | null },
  productTypeAliases?: Record<string, string>,
): string {
  if (group.fulfillmentType === 'P&P') return group.productName;
  return group.shopifyProductType
    ? (productTypeAliases?.[group.shopifyProductType] ?? group.shopifyProductType)
    : group.productName;
}

/**
 * ⚠ GROUP KEY CONTRACT: the key is reconstructed independently from
 * `pickProgress.items` in BatchDetailScreen (getBatchPickProgress selects
 * only id/sku/size/picked_at; pickSku, fulfillmentType AND printTechnology
 * come from the labels-keyed maps there). BOTH sides MUST build the key
 * through THIS function — never inline the template. `sku` is the SKU the
 * picker takes: `pickSku ?? sku` of the garment.
 */
export function buildPicklistGroupKey(
  sku: string,
  size: string | null | undefined,
  fulfillmentType: string | null | undefined,
  printTechnology: string | null | undefined,
): string {
  const ft = fulfillmentType || 'POD';
  const base = `${sku}-${size || ''}-${ft}`;
  // POD splits by print technology; PAP keys stay byte-identical to legacy.
  if (ft === 'P&P') return base;
  return `${base}-${printTechnology === 'DTF' ? 'DTF' : 'DTG'}`;
}

/** The picker takes a backup pile: another code than the order's, or a client's neck label. */
export function isBackupGroup(group: Pick<PicklistGroup, 'sku' | 'unitSku' | 'clientLabelled'>): boolean {
  return group.clientLabelled || group.sku !== group.unitSku;
}

/**
 * An order-change replacement unit: a redo row minted for an order change.
 * BOTH parts are required; a non-redo garment carrying the reason is neither
 * an order change nor a redo.
 */
function isOrderChangeUnit(g: GroupableGarment): boolean {
  return g.isRedo === true && g.redoReason === ORDER_CHANGE_BACKFILL_REASON;
}

/**
 * A genuine redo: isRedo with any other reason, including null, undefined,
 * or an empty string.
 */
function isGenuineRedo(g: GroupableGarment): boolean {
  return g.isRedo === true && g.redoReason !== ORDER_CHANGE_BACKFILL_REASON;
}

/**
 * The one distinct truthy value, or undefined when there are none or several.
 * A group's colour and pick photo both FAIL CLOSED through this: empty members
 * are ignored (['', 'Black'] -> 'Black'), disagreeing members give nothing.
 * Never "take the first".
 */
function singleDistinct<T>(values: readonly (T | null | undefined)[]): T | undefined {
  const distinct = [...new Set(values.filter((v): v is T => Boolean(v)))];
  return distinct.length === 1 ? distinct[0] : undefined;
}

/**
 * Group garments by SKU and size into a flat list.
 * Redos are included in same group as regular items.
 */
function groupBySkuAndSize(
  garments: GroupableGarment[],
  config: PicklistSortConfig
): PicklistGroup[] {
  const groups = new Map<string, GroupableGarment[]>();

  // Group by SKU + Size (redos mixed with regular items). POD keys carry the
  // print technology so DTG and DTF form separate groups (and thus separate
  // picking sections); PAP keys deliberately DON'T — keeping them byte-stable
  // with the key reconstructed from pickProgress in BatchDetailScreen.
  for (const garment of garments) {
    const key = buildPicklistGroupKey(
      garment.pickSku ?? garment.sku,
      garment.size,
      garment.fulfillmentType,
      garment.printTechnology,
    );
    const existing = groups.get(key) || [];
    existing.push(garment);
    groups.set(key, existing);
  }

  // Convert to PicklistGroup array
  const result: PicklistGroup[] = [];

  for (const [key, groupGarments] of groups.entries()) {
    const first = groupGarments[0];
    // Skip empty groups (shouldn't happen, but satisfies TypeScript)
    if (!first) continue;

    const orderNumbers = [
      ...new Set(groupGarments.map((g) => g.orderNumber).filter(Boolean)),
    ] as string[];

    // Count genuine redos and collect their reasons; order-change
    // replacement units are counted separately and never add a reason.
    const redoItems = groupGarments.filter(isGenuineRedo);
    const redoReasons = [
      ...new Set(redoItems.map((g) => g.redoReason).filter(Boolean)),
    ] as string[];
    const orderChangeCount = groupGarments.filter(isOrderChangeUnit).length;

    // Count priority injections (auto-added from resolved inventory errors)
    const priorityInjectionCount = groupGarments.filter((g) => g.isPriorityInjection).length;

    const clientLabelled = groupGarments.every((g) => g.clientLabelled === true);

    const group: PicklistGroup = {
      groupKey: key,
      sku: first.pickSku ?? first.sku,
      unitSku: first.sku,
      clientLabelled,
      clientName: clientLabelled ? singleDistinct(groupGarments.map((g) => g.clientName)) ?? null : null,
      shopifySku: first.shopifySku ?? null,
      productName: first.productName,
      size: first.size || '',
      color: singleDistinct(groupGarments.map((g) => g.color)) ?? '',
      quantity: groupGarments.length,
      redoCount: redoItems.length,
      orderChangeCount,
      requiresPretreat: first.requiresPretreat ?? false,
      redoReasons,
      garmentIds: groupGarments.map((g) => g.id),
      orderNumbers,
      binLabel: first.binLabel,
      shopifyProductType: first.shopifyProductType ?? null,
      fulfillmentType: first.fulfillmentType ?? 'POD',
      printTechnology: (first.fulfillmentType ?? 'POD') !== 'P&P'
        ? (first.printTechnology === 'DTF' ? 'DTF' : 'DTG')
        : 'DTG',
      priorityInjectionCount,
      pickImageUrl: singleDistinct(groupGarments.map((g) => g.pickImageUrl)) ?? null,
      sectionKey: '',
      sectionLabel: '',
    };
    group.sectionKey = getSectionKey(config, group);
    group.sectionLabel = getSectionLabel(config, group);
    result.push(group);
  }

  // Sort groups with the config-driven comparator (default config reproduces
  // the legacy P&P-last → size ladder → SKU order exactly).
  return result.sort(buildPicklistComparator(config));
}

/**
 * Group garments into a flat picklist (no sections).
 *
 * Single flat list in configured order - picker grabs everything at once.
 * Pretreat info is on each label's QR code.
 * Redos combined with same-SKU items.
 *
 * @param batchId - Batch ID
 * @param garments - All garments in the batch
 * @param config - Picklist sort config (defaults to today's legacy order)
 * @returns Flat grouped picklist
 *
 * @example
 * const grouped = groupPicklist('batch-123', garments, config);
 * // grouped.groups = [{ sku: 'HOODIE-BLK', quantity: 5, redoCount: 1, ... }, ...]
 */
export function groupPicklist(
  batchId: string,
  garments: GroupableGarment[],
  config: PicklistSortConfig = DEFAULT_PICKLIST_SORT
): GroupedPicklist {
  const groups = groupBySkuAndSize(garments, config);
  // Summary totals derive from the group counts so classification stays in
  // one place.
  let totalRedoCount = 0;
  let totalOrderChangeCount = 0;
  for (const group of groups) {
    totalRedoCount += group.redoCount;
    totalOrderChangeCount += group.orderChangeCount;
  }

  return {
    batchId,
    totalItems: garments.length,
    groups,
    summary: {
      totalGroups: groups.length,
      totalRedoCount,
      totalOrderChangeCount,
    },
  };
}
